import React, { useState, useMemo } from "react";
import { Container, Row, Col, Form, ListGroup } from "react-bootstrap";
import {
  FaSearch,
  FaUser,
  FaCreditCard,
  FaTools,
  FaChevronRight,
} from "react-icons/fa";
import { Link } from "react-router-dom";
import ArticleCard from "../components/ArticleCard/ArticleCard";
import ContactSupport from "../components/ContactSupport/ContactSupport";
import styled from "styled-components";
import { articles } from "../data/helpArticles";
import { getRelativeTimeString } from "../utils/dateUtils";
import { sanitizeSearchQuery } from "../utils/searchUtils";

const categories = [
  {
    title: "Account",
    articleCount: 3,
    icon: <FaUser />,
  },
  {
    title: "Billing",
    articleCount: 2,
    icon: <FaCreditCard />,
  },
  {
    title: "Troubleshooting",
    articleCount: 2,
    icon: <FaTools />,
  },
];

const SearchWrapper = styled.div`
  background-color: var(--primary-bg);
  padding: 4rem 0;
  margin-bottom: 2rem;
`;

const SearchContainer = styled.div`
  position: relative;
  max-width: 600px;
  margin: 0 auto;
`;

const SearchIcon = styled.span`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6c757d;
`;

const CenteredContainer = styled(Container)`
  max-width: 1000px;
`;

const SearchResults = styled.div`
  margin-bottom: 3rem;
`;

const StyledListGroup = styled(ListGroup)`
  margin-top: 1rem;

  .list-group-item {
    border-left: none;
    border-right: none;
    padding: 1rem 0;

    &:first-child {
      border-top: none;
    }

    &:last-child {
      border-bottom: none;
    }
  }
`;

const ArticleLink = styled(Link)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: inherit;
  text-decoration: none;
  padding: 0.5rem 0;

  &:hover {
    color: var(--primary-bg);
    text-decoration: none;

    .article-title {
      color: var(--primary-bg);
    }
  }
`;

const ArticleTitle = styled.span`
  font-weight: 500;
`;

const CategoryLabel = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
  margin-left: 0.5rem;
`;

const LastUpdated = styled.span`
  color: #6c757d;
  font-size: 0.9rem;
`;

const NoResults = styled.div`
  text-align: center;
  padding: 2rem;
  color: #6c757d;
`;

const HelpCenter: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedQuery, setDebouncedQuery] = useState("");

  // Debounce and sanitize the search input
  React.useEffect(() => {
    const timeoutId = setTimeout(() => {
      const sanitized = sanitizeSearchQuery(searchQuery);
      setDebouncedQuery(sanitized);
    }, 300); // Wait 300ms after user stops typing

    return () => clearTimeout(timeoutId);
  }, [searchQuery]);

  const searchResults = useMemo(() => {
    if (!debouncedQuery) return [];

    const query = debouncedQuery.toLowerCase();
    const results: Array<{
      category: string;
      article: {
        title: string;
        slug: string;
        lastUpdated: Date;
      };
    }> = [];

    try {
      Object.entries(articles).forEach(([category, categoryArticles]) => {
        Object.values(categoryArticles).forEach((article) => {
          if (
            article.title.toLowerCase().includes(query) ||
            article.content.some((content) =>
              content.toLowerCase().includes(query)
            )
          ) {
            results.push({
              category,
              article: {
                title: article.title,
                slug: article.slug,
                lastUpdated: article.lastUpdated,
              },
            });
          }
        });
      });
    } catch (error) {
      console.error("Error during search:", error);
      return [];
    }

    return results;
  }, [debouncedQuery]);

  return (
    <div>
      <SearchWrapper>
        <Container>
          <h1 className="text-center text-white mb-4">Help Center</h1>
          <SearchContainer>
            <Form onSubmit={(e) => e.preventDefault()}>
              <Form.Group>
                <SearchIcon>
                  <FaSearch />
                </SearchIcon>
                <Form.Control
                  type="search"
                  placeholder="Search articles..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  style={{ paddingLeft: "2.5rem" }}
                  className="form-control-lg"
                  maxLength={100}
                  aria-label="Search help articles"
                />
              </Form.Group>
            </Form>
          </SearchContainer>
        </Container>
      </SearchWrapper>

      <CenteredContainer>
        {debouncedQuery ? (
          <SearchResults>
            <h2 className="mb-4">Search Results</h2>
            {searchResults.length > 0 ? (
              <StyledListGroup>
                {searchResults.map(({ category, article }) => (
                  <ListGroup.Item key={`${category}-${article.slug}`}>
                    <ArticleLink to={`/help/${category}/${article.slug}`}>
                      <div>
                        <ArticleTitle className="article-title">
                          {article.title}
                          <CategoryLabel>
                            in{" "}
                            {category.charAt(0).toUpperCase() +
                              category.slice(1)}
                          </CategoryLabel>
                        </ArticleTitle>
                      </div>
                      <div className="d-flex align-items-center">
                        <LastUpdated>
                          Updated {getRelativeTimeString(article.lastUpdated)}
                        </LastUpdated>
                        <FaChevronRight className="ms-3" />
                      </div>
                    </ArticleLink>
                  </ListGroup.Item>
                ))}
              </StyledListGroup>
            ) : (
              <NoResults>
                <p>No articles found matching "{debouncedQuery}"</p>
                <p>
                  Try adjusting your search terms or browse through our
                  categories below
                </p>
              </NoResults>
            )}
          </SearchResults>
        ) : (
          <>
            <h2 className="mb-4 text-center">Browse by Topic</h2>
            <Row className="mb-5 justify-content-center">
              {categories.map((category) => (
                <Col
                  xs={12}
                  sm={6}
                  md={4}
                  key={category.title}
                  className="mb-4 d-flex justify-content-center"
                >
                  <div style={{ width: "100%", maxWidth: "300px" }}>
                    <ArticleCard
                      title={category.title}
                      articleCount={category.articleCount}
                      icon={category.icon}
                    />
                  </div>
                </Col>
              ))}
            </Row>
          </>
        )}

        <ContactSupport />
      </CenteredContainer>
    </div>
  );
};

export default HelpCenter;
